import PropTypes from 'prop-types';
import { useEffect, useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { BREADCRUMB_STICKY_TOP } from '../../layout/stickyOffsets';

// material-ui
import { useTheme } from '@mui/material/styles';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';
import MuiBreadcrumbs from '@mui/material/Breadcrumbs';
import Box from '@mui/material/Box';

// project imports
import { ThemeMode, ThemeDirection } from 'config';
import navigation from 'menu-items';
import useConfig from 'hooks/useConfig';
import { useTab } from '../../contexts/TabContext';
import { useOrganization } from '../../contexts/OrganizationContext';

// assets
import { IconChevronRight, IconTallymark1 } from '@tabler/icons-react';
import AccountTreeTwoToneIcon from '@mui/icons-material/AccountTreeTwoTone';
import HomeIcon from '@mui/icons-material/Home';
import HomeTwoToneIcon from '@mui/icons-material/HomeTwoTone';

// third-party
import { FormattedMessage, useIntl } from 'react-intl';
import TruncateTooltip from '../../commonComponent/TruncateTooltip';
import { fetchDeviceDashboardInfo } from 'utils/deviceDashboardInfoCache';
import { fetchOrbiSummary } from 'api/orbiService';
import { deviceStatusColor } from '../../store/constant';
import { Badge } from '@mui/material';
import constants from '../../utils/constants';
import DarkIcon from '../../assets/images/icons/building-teal-dark.svg'
import LightIcon from '../../assets/images/icons/building-teal.svg';
import LocDarkIcon from '../../assets/images/icons/location-teal-dark.svg';
import LocLightIcon from '../../assets/images/icons/location-teal-light.svg';
import { IconUserCircle } from '@tabler/icons-react';

/** Message id in locale JSON (e.g. newLoginAllOrganizations → "All Organizations" in en.json). */
const ALL_ORGANIZATIONS_INTL_ID = 'newLoginAllOrganizations';

// ==============================|| BREADCRUMBS TITLE ||============================== //

function BTitle({ title }) {
  const [isDevice, setIsDevice] = useState(false);
  const [deviceInfo, setDeviceInfo] = useState({});
  const currentDeviceRef = useRef({ deviceId: null, deviceType: null });
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    let isCancelled = false;
    
    // Extract device info from pathname
    const isDevicePath = location.pathname.startsWith('/devices/');
    const currentDeviceId = localStorage.getItem("selectedDeviceId");
    const currentDeviceType = isDevicePath ? location.pathname.split('/')[2] : null;
    
    // Only reset if we're navigating away from a device page
    if (!isDevicePath) {
      setIsDevice(false);
      setDeviceInfo({});
      currentDeviceRef.current = { deviceId: null, deviceType: null };
    } else if (isDevicePath && currentDeviceId && currentDeviceType) {
      // Check if we're still on the same device (just switching tabs)
      const isSameDevice = currentDeviceRef.current.deviceId === currentDeviceId &&
                           currentDeviceRef.current.deviceType === currentDeviceType;
      
      if (isSameDevice) {
        // Same device, different tab - don't refetch, device info already loaded
        return;
      }
      
      // Different device or first load - fetch device info
      const fetchDeviceInfo = async (deviceType) => {
        try {
          // The device's own Summary tab fetches this identical endpoint independently — route
          // through the shared cache so the two coalesce into one call. ORBI already has its
          // own coalescing helper (shared with OrbiDeviceContext/orbi summary); reuse that.
          const response =
            deviceType === 'ORBI'
              ? await fetchOrbiSummary(currentDeviceId)
              : await fetchDeviceDashboardInfo(
                  'GET_BR_SUMMARY_DATA',
                  { selectedDeviceId: currentDeviceId, selectedDeviceType: deviceType },
                  currentDeviceId,
                  deviceType
                );

          // Ignore stale responses if component navigated away
          if (!isCancelled) {
            if (response.response.status) {
              setIsDevice(true);
              setDeviceInfo(response.deviceInfo);
              // Only update ref after successful fetch to prevent skipping retries on failure
              currentDeviceRef.current = { deviceId: currentDeviceId, deviceType: currentDeviceType };
            } else {
              setIsDevice(false);
              setDeviceInfo({});
            }
          }
        } catch (error) {
          if (!isCancelled) {
            setIsDevice(false);
            setDeviceInfo({});
          }
        }
      };
      
      fetchDeviceInfo(currentDeviceType);
    }
    
    // Cleanup function to cancel stale responses
    return () => {
      isCancelled = true;
    };
  }, [location.pathname]);

  // On device path, show device name: from API when available, else from localStorage (e.g. MHS or before API returns)
  const isDevicePath = location.pathname.startsWith('/devices/');
  let deviceDisplayName = null;
  if (isDevice && deviceInfo?.deviceName) {
    deviceDisplayName = deviceInfo.deviceName;
  } else if (isDevicePath) {
    try {
      const stored = JSON.parse(localStorage.getItem(constants.SELECTED_DEVICE) || '{}');
      deviceDisplayName = stored?.deviceName || stored?.name || stored?.serialNo || null;
    } catch {
      deviceDisplayName = null;
    }
  }

  // Stack detail routes: show stack name in the breadcrumb/title row (mirrors standalone device pages).
  // The stack name is persisted on stack click (handleStackNameClick) and refreshed after detail load.
  const isStackPath = location.pathname.startsWith('/locationDevice/stack/');
  const stackIdMatch = isStackPath ? location.pathname.match(/^\/locationDevice\/stack\/([^/]+)/) : null;
  const stackIdInPath = stackIdMatch?.[1] || null;
  const storedStackId = isStackPath ? localStorage.getItem('selectedStackId') : null;
  const stackDisplayName =
    isStackPath && stackIdInPath && storedStackId && String(storedStackId) === String(stackIdInPath)
      ? localStorage.getItem('selectedStackName')
      : null;

  // Check if title is a translation key
  // Examples: "manageSubscriptions", "Dashboard", "Locations", etc.
  // Matches camelCase (starting with lowercase) or PascalCase (starting with uppercase) without spaces
  const isTranslationKey = title &&
    typeof title === 'string' &&
    /^[a-zA-Z][a-zA-Z0-9]*$/.test(title) &&
    !title.includes(' ') &&
    title.length > 0;

  if (stackDisplayName) {
    return (
      <Grid>
        <Typography variant="h2" sx={{ display: 'flex', alignItems: 'center' }}>
          {stackDisplayName}
        </Typography>
      </Grid>
    );
  }

  if (deviceDisplayName) {
    const pathParts = location.pathname.split('/').filter(Boolean);
    const deviceType = pathParts[1] || ''; // e.g. MHS, AP, SW
    return (
      <Grid>
        <Typography variant="h2" sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
          {deviceInfo?.priStatus != null && (
            <Badge
              id="breadcrumb-deviceStatus-badge"
              color="secondary"
              variant="dot"
              sx={{
                '.MuiBadge-badge': {
                  top: 0.25,
                  left: '0px',
                  backgroundColor: deviceStatusColor[deviceInfo?.priStatus || 0]
                }
              }}
            />
          )}
          <TruncateTooltip sx={{maxWidth: '200px'}} placement="bottom" variant="h2">{deviceDisplayName}</TruncateTooltip>
        </Typography>
      </Grid>
    );
  }

  return (
    <Grid>
      <Typography variant="h2">
        {isTranslationKey ? <FormattedMessage id={title} defaultMessage={title} /> : title}
      </Typography>
    </Grid>
  );
}

// Custom breadcrumb path generator based on current route and active tab
function getBreadcrumbPath(pathname, activeTab) {
   // Get scopeId from localStorage to determine organization level
   const scopeId = localStorage.getItem('scopeId') || '0';
   // Special handling for Wi-Fi feature routes to include feature in breadcrumb
  if (pathname.startsWith('/Wi-Fi')) {
    const savedOrgName = localStorage.getItem("savedOrgName") || localStorage.getItem("currentOrgName");
    const cleanOrgName = (savedOrgName || 'Organization').trim().replace(/\/+$/, '');
    const orgDashboardUrl = savedOrgName
      ? `/organization/${encodeURIComponent(cleanOrgName)}/dashboard`
      : '/mspHome/dashboard';
    let selectedType = {};
    try {
      selectedType = JSON.parse(localStorage.getItem("selectedType") || '{}');
    } catch {
      selectedType = {};
    }
    const rawLocationLabel = selectedType?.label || localStorage.getItem("savedLocationName") || 'Location';
    const locationLabel = rawLocationLabel.replace(/\//g, ' / ');
    return [
      { title: `${ALL_ORGANIZATIONS_INTL_ID}/`, to: '/mspHome/dashboard', isLast: false, action: 'msp' },
      { title: `${cleanOrgName}/`, to: orgDashboardUrl, isLast: false, action: 'org' },
      { title: locationLabel, to: '/dashboard', isLast: false, action: 'location' },
      { title: 'Wi-Fi', to: '/Wi-Fi', isLast: true }
    ];
  }
  // Notifications page - always show All Organizations / Notifications
  if (pathname === '/notifications' || pathname === '/mspHome/notifications' || (pathname.startsWith('/organization/') && pathname.endsWith('/notifications'))) {
    return [
      { title: `${ALL_ORGANIZATIONS_INTL_ID}/`, to: '/mspHome/dashboard', isLast: false, action: 'msp' },
      { title: 'Alarms', to: '/notifications', isLast: true }
    ];
  }

  // MSP Administration sub-routes — MSP Home / Administration / (page title from menu, e.g. Cloud REST API)
  if (pathname.startsWith('/mspHome/administration')) {
    return [
      { title: 'MSP Home/', to: '/mspHome/dashboard', isLast: false, action: 'msp' },
      { title: 'Administration/', to: '/mspHome/administration/users', isLast: true, action: 'msp' }
    ];
  }

  // Organization routes - show All Organizations/Organization/ path
  if (pathname.startsWith('/organization/')) {
    const pathParts = pathname.split('/').filter(Boolean);
    if (pathParts.length >= 2 && pathParts[0] === 'organization') {
      const orgName = decodeURIComponent(pathParts[1]);
      const savedOrgName = localStorage.getItem("savedOrgName") || orgName;
      // Remove any trailing slashes or duplicates from savedOrgName
      const cleanOrgName = savedOrgName.trim().replace(/\/+$/, '');
      return [
        { title: `${ALL_ORGANIZATIONS_INTL_ID}/`, to: '/mspHome/dashboard', isLast: false, action: 'msp' },
        { title: `${cleanOrgName}/`, to: `/organization/${encodeURIComponent(orgName)}/dashboard`, isLast: false, action: 'org' }
      ];
    }
  }
  
  // Legacy organization routes (if any still exist)
  if (pathname.startsWith('/organizations')) {
    return [
      { title: 'MSP Home/', to: '/mspHome/dashboard', isLast: false },
      { title: 'Organization/', to: '/organizations', isLast: true }
    ];
  }

  // Device detail routes: All Organizations / Org / Location / [Device Name] (device name is last, no "Devices" segment)
  if (pathname.startsWith('/devices/')) {
    const savedOrgName = localStorage.getItem('savedOrgName') || localStorage.getItem('currentOrgName');
    let selectedType = {};
    try {
      selectedType = JSON.parse(localStorage.getItem('selectedType') || '{}');
    } catch {
      selectedType = {};
    }
    const locationLabel = selectedType?.label || 'Location';
    const cleanOrgName = (savedOrgName || 'Organization').trim().replace(/\/+$/, '');
    const orgDashboardUrl = savedOrgName
      ? `/organization/${encodeURIComponent(cleanOrgName)}/dashboard`
      : '/mspHome/dashboard';
    let deviceName = 'Device';
    try {
      const stored = JSON.parse(localStorage.getItem(constants.SELECTED_DEVICE) || '{}');
      deviceName = stored?.deviceName || stored?.name || stored?.serialNo || 'Device';
    } catch {
      deviceName = 'Device';
    }
    const pathParts = pathname.split('/').filter(Boolean);
    const deviceType = pathParts[1];
    const deviceDashboardTo = deviceType ? `/devices/${deviceType}/summary` : pathname;
    return [
      { title: `${ALL_ORGANIZATIONS_INTL_ID}/`, to: '/mspHome/dashboard', isLast: false, action: 'msp' },
      { title: `${cleanOrgName}/`, to: orgDashboardUrl, isLast: false, action: 'org' },
      { title: `${locationLabel}/`, to: '/dashboard', isLast: false, action: 'location' },
      { title: deviceName, to: deviceDashboardTo, isLast: true, action: 'device' }
    ];
  }

  // MSP Home routes and default - show All Organizations/ path
  if (pathname.startsWith('/mspHome/')) {
    if (scopeId === '0') {
      return [
        { title: ALL_ORGANIZATIONS_INTL_ID, to: '/mspHome/dashboard', isLast: true, action: 'msp' }
      ];
    } else if (scopeId === '1') {
      const savedOrgName = localStorage.getItem("savedOrgName") || localStorage.getItem("currentOrgName");
      const displayOrgName = savedOrgName || 'Organization';
      const orgDashboardUrl = savedOrgName
        ? `/organization/${encodeURIComponent(displayOrgName)}/dashboard`
        : '/mspHome/dashboard';
      return [
        { title: `${ALL_ORGANIZATIONS_INTL_ID}/`, to: '/mspHome/dashboard', isLast: false, action: 'msp' },
        { title: displayOrgName, to: orgDashboardUrl, isLast: true, action: 'org' }
      ];
    }
    // Default fallback
    return [
      { title: `${ALL_ORGANIZATIONS_INTL_ID}/`, to: '/mspHome/dashboard', isLast: false, action: 'msp' }
    ];
  } else {
    // Get scope level to determine if we're at organization or location level
    const scopeId = localStorage.getItem("scopeId");
    const savedOrgName = localStorage.getItem("savedOrgName") || localStorage.getItem("currentOrgName");
    let selectedType = {};
    try {
      selectedType = JSON.parse(localStorage.getItem("selectedType") || '{}');
    } catch {
      selectedType = {};
    }
    // If pathname is /dashboard or /dashboard/*, determine level based on scopeId
    if (pathname === '/dashboard' || pathname.startsWith('/dashboard/')) {
      // Organization level: scopeId === '1'
      if (scopeId === '1') {
        const cleanOrgName = (savedOrgName || 'Organization').trim().replace(/\/+$/, '');
        const orgDashboardUrl = savedOrgName
          ? `/organization/${encodeURIComponent(cleanOrgName)}/dashboard`
          : '/mspHome/dashboard';
        return [
          { title: `${ALL_ORGANIZATIONS_INTL_ID}/`, to: '/mspHome/dashboard', isLast: false, action: 'msp' },
          { title: `${cleanOrgName}/`, to: orgDashboardUrl, isLast: false, action: 'org' }
        ];
      }
      else if (scopeId === '2' || (scopeId && scopeId !== '0' && scopeId !== '1')) {
        const locationLabel = selectedType?.label || 'Location';
        const cleanOrgName = (savedOrgName || 'Organization').trim().replace(/\/+$/, '');
        const orgDashboardUrl = savedOrgName
          ? `/organization/${encodeURIComponent(cleanOrgName)}/dashboard`
          : '/mspHome/dashboard';
        return [
          { title: `${ALL_ORGANIZATIONS_INTL_ID}/`, to: '/mspHome/dashboard', isLast: false, action: 'msp' },
          { title: `${cleanOrgName}/`, to: orgDashboardUrl, isLast: false, action: 'org' },
          { title: locationLabel, to: '/dashboard', isLast: false, action: 'location' }
        ];
      }
    }
    // below is the case for device-group routes
    if (pathname.startsWith('/device-group')) {
      const orgName = (savedOrgName || 'Organization').trim().replace(/\/+$/, '');
      const orgDashboardUrl = savedOrgName
        ? `/organization/${encodeURIComponent(orgName)}/dashboard`
        : '/mspHome/dashboard';
      const locationName = localStorage.getItem("savedLocationName") || "Location";
      const deviceGroupName = selectedType?.label || 'DeviceGroup';
      return [
        { title: `${ALL_ORGANIZATIONS_INTL_ID}/`, to: '/mspHome/dashboard', isLast: false, action: 'msp' },
        { title: `${orgName}/`, to: orgDashboardUrl, isLast: false, action: 'org' },
        { title: locationName, to: '/dashboard', isLast: false, action: 'location' },
        { title: deviceGroupName, to: '/device-group/devices', isLast: false, action: 'location' }
      ];
    }
    const locationLabel = selectedType?.label || 'Location';
    const finalOrgName = savedOrgName || 'Organization';
    const cleanFinalOrgName = finalOrgName.trim().replace(/\/+$/, '');
    const finalOrgDashboardUrl = savedOrgName
      ? `/organization/${encodeURIComponent(cleanFinalOrgName)}/dashboard`
      : '/mspHome/dashboard';
    
    return [
      { title: `${ALL_ORGANIZATIONS_INTL_ID}/`, to: '/mspHome/dashboard', isLast: false, action: 'msp' },
      { title: `${cleanFinalOrgName}/`, to: finalOrgDashboardUrl, isLast: false, action: 'org' },
      { title: locationLabel, to: '/dashboard', isLast: false, action: 'location' }
    ];

  }
}

// Get page title based on active tab
function getPageTitle(pathname, activeTab) {
  if (pathname.startsWith('/locationSettings/firmware')) {
    return 'firmware';
  }

  // Use the active tab name from context if available
  if (activeTab && activeTab.tabName) {
    return activeTab.tabName;
  }
  
  // Fallback to pathname-based title
  let pageTitle = pathname.split('/').filter(segment => segment).pop();
  if (pageTitle == 'locationDevice') {
    return 'Devices';
  }
  if (
    pageTitle === 'MobileProfile' ||
    pageTitle === 'MobileHotspot' ||
    pageTitle === 'mobile-profiles' ||
    pageTitle === 'geofence'
  ) {
    return 'mobileHotspot';
  }
  if(pageTitle == 'ManagementVLAN'){
    return 'Management VLAN'
  } else if(pageTitle == 'lag'){
    return 'LAG'
  } else if(pageTitle == 'spanningTree'){
    return 'Spanning Tree'
  } else if(pageTitle == 'radiusConfiguration'){
    return 'RADIUS Configuration'
  }
  return pageTitle.charAt(0).toUpperCase() + pageTitle.slice(1)
}

export default function Breadcrumbs({
  card,
  custom = false,
  divider = false,
  headerActions = null,
  heading,
  icon = true,
  icons,
  links,
  maxItems,
  rightAlign = true,
  separator = IconChevronRight,
  title = true,
  titleBottom,
  sx,
  ...others
}) {
  const theme = useTheme();
  const location = useLocation();
  const { themeDirection } = useConfig();
  const intl = useIntl();
  const allOrganizationsLabel = intl.formatMessage({ id: 'allOrganizations' });
  const [main, setMain] = useState();
  const [item, setItem] = useState();
  const navigate = useNavigate();
  const { updateOrganizationContext } = useOrganization();
  
  // Handle breadcrumb navigation with organization context update
  const handleBreadcrumbClick = (crumb) => {
    if (crumb.isLast || !crumb.to) return; // Don't navigate if it's the last breadcrumb or no URL
    if (crumb.action === 'msp') {
      // Navigate to MSP Home / All Organizations
      localStorage.setItem("scopeId", 0);
      updateOrganizationContext(null, '0', null, {
        id: "All Organizations",
        label: allOrganizationsLabel,
        type: "All Organizations",
        icon: "All Organizations"
      });
      navigate('/mspHome/dashboard');  // navigating to the msp home when the All Organizations is clicked in BreadCrumbs
    } else if (crumb.action === 'org') {
      // Navigate to organization level
      const pathParts = crumb.to.split('/');
      // Get org ID from localStorage or use orgName as fallback
      const savedOrgId = localStorage.getItem('OrgId');
      updateOrganizationContext(savedOrgId, '1', null, {
        id: "Organization-wide",
        label: localStorage.getItem("savedOrgName"),
        type: "Organization-wide",
        icon: "Org"
      });
      navigate(crumb.to); // navigating to the organization-wide dashboard when the organization breadcrumb is clicked
    }
    else if (crumb.action === 'location') {
      navigate(crumb.to); // navigating to the location dashboard when the location breadcrumb is clicked
    }
  };
  
  // Safely get activeTab, handle case where TabContext might not be available
  let activeTab = null;
  try {
    const tabContext = useTab();
    activeTab = tabContext.activeTab;
  } catch (error) {
    // TabContext not available, continue with null activeTab
  }

  const iconSX = {
    marginRight: 6,
    marginTop: -2,
    width: '1rem',
    height: '1rem',
    color: theme.palette.secondary.main
  };

  const linkSX = {
    display: 'flex',
    color: 'grey.600',
    textDecoration: 'none',
    alignContent: 'center',
    alignItems: 'center',
    fontSize: '0.875rem',
    fontWeight: 400,
    '&:hover': {
      color: 'primary.main'
    }
  };

  let customLocation = location.pathname;

  useEffect(() => {
    navigation?.items?.map((menu) => {
      if (menu.type && menu.type === 'group') {
        if (menu?.url && menu.url === customLocation) {
          setMain(menu);
          setItem(menu);
        } else {
          getCollapse(menu);
        }
      }
      return false;
    });
  });

  // set active item state
  const getCollapse = (menu) => {
    if (!custom && menu.children) {
      menu.children.filter((collapse) => {
        if (collapse.type && collapse.type === 'collapse') {
          getCollapse(collapse);
          if (collapse.url === customLocation) {
            setMain(collapse);
            setItem(collapse);
          }
        } else if (collapse.type && collapse.type === 'item') {
          if (customLocation === collapse.url) {
            setMain(menu);
            setItem(collapse);
          }
        }
        return false;
      });
    }
  };

  // Get breadcrumb path and page title based on current route and active tab
  const breadcrumbPath = getBreadcrumbPath(customLocation, activeTab);
  // On device detail pages (/devices/...), don't use menu item title (e.g. "Devices" from list); use tab title or let BTitle show device name
  const isMobileHotspotSection = customLocation?.startsWith('/MobileProfile');
  const pageTitle = customLocation.startsWith('/devices/') || customLocation.startsWith('/locationSettings/firmware')
    ? getPageTitle(customLocation, activeTab)
    : isMobileHotspotSection
      ? 'mobileHotspot'
      : (item?.title || getPageTitle(customLocation, activeTab));

  // Hide breadcrumbs for Support Request page (it has its own breadcrumbs)
  if (customLocation === '/support/usersList') {
    return null;
  }
// Determine scope label based on scopeId in localStorage
  const scopeId = localStorage.getItem('scopeId');
  const scopeLabel =
  scopeId === '0'
      ? <Box component="div" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} id="scope-label-data">
        <Typography sx={{ height: '16px' }} id="scope-label-icon-acc"><IconUserCircle size={16} stroke={1.5} /></Typography>
        <FormattedMessage id="accountScope" defaultMessage="Account" />
      </Box>
    :scopeId === '1'
    ? <Box component="div" sx={{display:'flex', alignItems:'center', gap: 0.5}} id="scope-label-data">
      <Typography sx={{height: '16px'}} id="scope-label-icon-org"><Box component="img" src={theme.palette.mode === 'dark' ? DarkIcon : LightIcon} alt="Building icon" sx={{ width: '14px',}} />
</Typography>
      <FormattedMessage id="organizationScope" defaultMessage="Organization" />
      </Box>
    : scopeId === '2'
        ? <Box component="div" sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }} id="scope-label-data">
          <Typography sx={{ height: '16px' }} id="scope-label-icon-loc"><Box component="img" src={theme.palette.mode === 'dark' ? LocDarkIcon : LocLightIcon} alt="Location icon" sx={{ width: '10px', }} />
          </Typography>
          <FormattedMessage id="locationScope" defaultMessage="Location" />
        </Box>
      : null;

  // Hindsight: in-page MainCard already provides the title and context; hide breadcrumb strip here only.
  if (customLocation === '/hindsight') {
    return null;
  }

  // Custom breadcrumb content based on Figma design
  const breadcrumbContent = (
    <Box id="breadcrumb-container" 
    sx={{ 
      pb: 2.5,
      position: 'sticky',
      top: BREADCRUMB_STICKY_TOP,
      zIndex: 10,
      bgcolor: theme.palette.mode === ThemeMode.DARK ? theme.palette.dark[800] : theme.palette.grey[50],
      boxShadow: theme.palette.mode === ThemeMode.DARK ? `0px -18px 0px 0px ${theme.palette.dark[800]}` : `0px -18px 0px 0px ${theme.palette.grey[50]}`,
       ...sx 
      }} 
       {...others}
       >
      <Box sx={{ p: 0 }}>
        <Grid container direction="column" spacing={0}>
          {/* Breadcrumb Path */}
          <Grid  sx={{display: 'flex', gap: 1.5, mb: 1, flexWrap: 'wrap', alignItems: 'center'}}>
            <Box sx={{ mb: 0 }}>
              {breadcrumbPath.map((crumb, index) => {
                // Split the title by '/' and filter out empty strings
                const parts = crumb.title.split('/').filter(Boolean);
                return (
                  <Typography component='span' key={index} sx={{ display: 'inline-flex', alignItems: 'center'}}>
                    {parts.map((part, i) => (
                        <Typography
                          key={i}
                        component={crumb.isLast && i === parts.length - 1 ? 'span' : 'button'}
                        onClick={() => i === parts.length - 1 && !crumb.isLast ? handleBreadcrumbClick(crumb) : undefined}
                          sx={{
                            ...linkSX,
                            display: 'inline',
                            color: 'grey.600',
                            fontSize: theme.typography.body1.fontSize,
                            border: 'none',
                            background: 'none',
                            padding: 0,
                          cursor: crumb.isLast && i === parts.length - 1 ? 'default' : 'pointer',
                          '&:hover': crumb.isLast && i === parts.length - 1 ? {} : {
                              color: 'primary.main'
                            }
                          }}
                        >
                        <TruncateTooltip sx={{ maxWidth: '200px' }}>
                          {part === ALL_ORGANIZATIONS_INTL_ID ? (
                            <FormattedMessage id={ALL_ORGANIZATIONS_INTL_ID} defaultMessage="All Organizations" />
                          ) : part === 'MSP Home' ? (
                            <FormattedMessage id="MSP Home" defaultMessage="MSP Home" />
                          ) : part === 'Administration' ? (
                            <FormattedMessage id="Administration" defaultMessage="Administration" />
                          ) : (
                            part
                          )}
                        </TruncateTooltip>
                        </Typography>
                    ))}
                    {/* Add separator except after the last breadcrumb */}
                    {index < breadcrumbPath.length - 1 && (
                      <Typography component='span' sx={{ margin: '0 3px'}}>/</Typography>
                    )}
                  </Typography>
                );
              })}
            </Box>
            {scopeLabel && (
              <Typography
                variant="caption"
                component="div"
                id='scope-label-data-container'
                sx={{
                  py: 0.5,
                  px: 1,
                  borderRadius: 3,
                  color: theme => theme.palette.mode === ThemeMode.DARK ? '#4db6ac' : theme.palette.teal[600],
                  backgroundColor: 'bluegrey.light',
                  lineHeight: '16px',
                }}
              >
                {scopeLabel}
              </Typography>
            )}
          </Grid>
          
          {/* Page Title row: title on left, optional headerActions (e.g. Reboot/More) on right */}
          <Grid>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
              <BTitle title={pageTitle} />
              {headerActions != null ? <Box sx={{ flexShrink: 0 }}>{headerActions}</Box> : null}
            </Box>
          </Grid>
        </Grid>
      </Box>
      {divider !== false && <Divider sx={{ mt: 2 }} />}
    </Box>
  );

  // Handle custom breadcrumbs if provided
  if (custom && links && links?.length > 0) {
    const customBreadcrumbContent = (
      <Box sx={{ mb: 3, ...sx }} {...others}>
        <Box sx={{ p: 0 }}>
          <Grid container direction="column" spacing={0}>
            {/* Custom Breadcrumb Path */}
            <Grid>
              <Box sx={{ mb: 1 }}>
                {links.slice(0, -1).map((link, index) => (
                  <Typography
                    key={index}
                    component={link.to ? 'button' : 'span'}
                    onClick={() => link.to && handleBreadcrumbClick(link)}
                    sx={{
                      ...linkSX,
                      display: 'inline',
                      color: 'grey.700',
                      fontSize: theme.typography.body1.fontSize,
                      border: 'none',
                      background: 'none',
                      padding: 0,
                      cursor: link.to ? 'pointer' : 'default',
                      '&:hover': link.to ? {
                        color: 'primary.main'
                      } : {}
                    }}
                  >
                    {link.title === ALL_ORGANIZATIONS_INTL_ID || link.title === 'All Organizations' ? (
                      <FormattedMessage id={ALL_ORGANIZATIONS_INTL_ID} defaultMessage="All Organizations" />
                    ) : (
                      link.title
                    )}
                    {!link.title.endsWith('/') && index < links.length - 2 && '/'}
                  </Typography>
                ))}
              </Box>
            </Grid>
            
            {/* Custom Page Title */}
            <Grid>
              <BTitle title={heading || (links[links.length - 1]?.title)} />
            </Grid>
          </Grid>
        </Box>
        {divider !== false && <Divider sx={{ mt: 2 }} />}
      </Box>
    );
    
    return customBreadcrumbContent;
  }

  return breadcrumbContent;
}

BTitle.propTypes = { title: PropTypes.string };

Breadcrumbs.propTypes = {
  card: PropTypes.bool,
  custom: PropTypes.bool,
  divider: PropTypes.bool,
  headerActions: PropTypes.node,
  heading: PropTypes.string,
  icon: PropTypes.bool,
  icons: PropTypes.bool,
  links: PropTypes.array,
  maxItems: PropTypes.number,
  rightAlign: PropTypes.bool,
  separator: PropTypes.any,
  IconChevronRight: PropTypes.any,
  title: PropTypes.bool,
  titleBottom: PropTypes.bool,
  sx: PropTypes.any,
  others: PropTypes.any
};
